import type { ShowSasCallbacks, VerificationRequest, Verifier } from 'matrix-js-sdk/src/crypto-api';
import { VerifierEvent, VerificationRequestEvent } from 'matrix-js-sdk/src/crypto-api';
import { useCallback, useEffect, useRef, useState } from 'react';

import logger from 'utils/logging/faro';

export type UseSasCallbacksReturn = {
  sasCallbacks: ShowSasCallbacks | null;
};

type VerifierState = {
  verifier: Verifier | null;
  showSasHandler: (() => void) | null;
  requestChangeHandler: (() => void) | null;
  verifyCalledForRequest: VerificationRequest | null;
};

const useSasCallbacks = (activeRequest: VerificationRequest | null): UseSasCallbacksReturn => {
  const [sasCallbacks, setSasCallbacks] = useState<ShowSasCallbacks | null>(null);
  const verifierStateRef = useRef<VerifierState>({
    verifier: null,
    showSasHandler: null,
    requestChangeHandler: null,
    verifyCalledForRequest: null,
  });

  const detachVerifierListeners = useCallback(() => {
    const state = verifierStateRef.current;
    const verifier = state.verifier as Verifier;
    if (verifier?.off && state.showSasHandler) verifier.off(VerifierEvent.ShowSas, state.showSasHandler);

    verifierStateRef.current = {
      verifier: null,
      showSasHandler: null,
      requestChangeHandler: state.requestChangeHandler,
      verifyCalledForRequest: null,
    };
  }, []);

  const attachToVerifier = useCallback(
    (request: VerificationRequest) => {
      const state = verifierStateRef.current;
      if (!request.verifier || (state.verifier === request.verifier && state.showSasHandler)) return;

      detachVerifierListeners();

      const handleShowSas = () => {
        const callbacks = request.verifier?.getShowSasCallbacks();
        if (callbacks) setSasCallbacks(callbacks);
      };

      request.verifier.on(VerifierEvent.ShowSas, handleShowSas);
      verifierStateRef.current = {
        verifier: request.verifier,
        showSasHandler: handleShowSas,
        requestChangeHandler: state.requestChangeHandler,
        verifyCalledForRequest: request,
      };

      const immediateSas = request.verifier.getShowSasCallbacks();
      if (immediateSas) return setSasCallbacks(immediateSas);

      if (state.verifyCalledForRequest !== request) {
        verifierStateRef.current.verifyCalledForRequest = request;
        request.verifier.verify().catch((err) => {
          logger.error('SasVerification: verify() error', err);
          if (verifierStateRef.current.verifyCalledForRequest === request) {
            verifierStateRef.current.verifyCalledForRequest = null;
          }
        });
      }
    },
    [detachVerifierListeners]
  );

  useEffect(() => {
    if (!activeRequest) {
      setSasCallbacks(null);
      detachVerifierListeners();
      verifierStateRef.current.verifyCalledForRequest = null;
      return;
    }

    const state = verifierStateRef.current;
    if (state.verifyCalledForRequest && state.verifyCalledForRequest !== activeRequest) {
      state.verifyCalledForRequest = null;
    }

    if (activeRequest.verifier) attachToVerifier(activeRequest);

    const onRequestChange = () => {
      if (activeRequest.verifier && state.verifier !== activeRequest.verifier) attachToVerifier(activeRequest);
    };

    if (state.requestChangeHandler) activeRequest.off(VerificationRequestEvent.Change, state.requestChangeHandler);

    activeRequest.on(VerificationRequestEvent.Change, onRequestChange);
    verifierStateRef.current.requestChangeHandler = onRequestChange;

    return () => {
      detachVerifierListeners();
      if (activeRequest) activeRequest.off(VerificationRequestEvent.Change, onRequestChange);
      verifierStateRef.current.requestChangeHandler = null;
    };
  }, [activeRequest, attachToVerifier, detachVerifierListeners]);

  return {
    sasCallbacks,
  };
};

export default useSasCallbacks;
